import { useGetContractProductAttachments, useGetContractProductImages } from "@/services/Contracts";
import { useGetUserById } from "@/services/Login";
import { useGetCompanySpecificServices } from "@/services/Company";
import { CompanySpecificProduct, CompanySpecificService, Contract, PlatformService } from "@/types";
import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import Accordion from "@/components/atoms/accordions/DrawerAccordion";
import Group from "@/components/atoms/Group";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import DocumentList from "@/components/molecules/DocumentList";
import ImageCarousel from "@/components/molecules/ImageCarousel";
import DetailRow from "@/components/molecules/DrawerDetails/DetailsRow";
import { FC } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { convertAmountBetweenUnits, getContractTradeType } from "@/utils/Contracts";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import ContractEndTypeEnum from "@/constants/enums/ContractEndTypeEnum";

interface Props {
  contract: Contract;
}

const ContractDetailsTab: FC<Props> = ({ contract }) => {
  const { t } = useTranslation();

  const AccordionItems = [
    {
      value: "product" as const,
      label: t("tasks:approveContract.commodity"),
      controller: (
        <Accordion.Control>
          <Group>
            <Text size="sm" display="technical" tt="uppercase" strong={true}>
              {t("tasks:approveContract.commodity")}
            </Text>
          </Group>
        </Accordion.Control>
      ),
      panel: (
        <Accordion.Panel>
          <ContractCommodityDetails product={contract.contractProduct.companySpecificProduct} t={t} />
        </Accordion.Panel>
      ),
    },
    {
      value: "details" as const,
      label: t("tasks:approveContract.details"),
      controller: (
        <Accordion.Control>
          <Group>
            <Text size="sm" display="technical" tt="uppercase" strong={true}>
              {t("tasks:approveContract.details")}
            </Text>
          </Group>
        </Accordion.Control>
      ),
      panel: (
        <Accordion.Panel>
          <ContractDetails contract={contract} />
        </Accordion.Panel>
      ),
    },
    {
      value: "priceAndInvoicing" as const,
      label: t("tasks:approveContract.priceAndInvoicing"),
      controller: (
        <Accordion.Control>
          <Text size="sm" display="technical" tt="uppercase" strong={true}>
            {t("tasks:approveContract.priceAndInvoicing")}
          </Text>
        </Accordion.Control>
      ),
      panel: (
        <Accordion.Panel>
          <PriceAndInvoicingDetails contract={contract} />
        </Accordion.Panel>
      ),
    },
    {
      value: "transport" as const,
      label: t("tasks:approveContract.transport"),
      controller: (
        <Accordion.Control>
          <Text size="sm" display="technical" tt="uppercase" strong={true}>
            {t("tasks:approveContract.transport")}
          </Text>
        </Accordion.Control>
      ),
      panel: (
        <Accordion.Panel>
          <TransportDetails contract={contract} />
        </Accordion.Panel>
      ),
    },
  ];

  return (
    <Accordion unstyled defaultValue={AccordionItems[0]?.value} style={{}}>
      {AccordionItems.map((item, index) => (
        <Accordion.Item value={item?.value} key={index}>
          {item.controller}
          {item.panel}
        </Accordion.Item>
      ))}
    </Accordion>
  );
};

export default ContractDetailsTab;

const ContractCommodityDetails = ({ product, t }: { product: CompanySpecificProduct; t: any }) => {
  const {
    data: images,
    isLoading: isLoadingImages,
    isPending: isPendingImages,
  } = useGetContractProductImages(product.id, product.images);

  const { data: attachments, isLoading: isLoadingAttachments } = useGetContractProductAttachments(
    product.id,
    product.attachments,
    {
      // fix to not being able to ask for both at the same time, causing contracts pod to crash
      enabled: !isLoadingImages && !isPendingImages,
    }
  );

  return (
    <Grid>
      <DetailRow label="common:product" value={product.companySpecificProductName} />
      <DetailRow label="" value={product.properties.map((property) => property.value).join(", ")} />
      {/*TODO => images and attachments need to be shown when BE is ready to display regardless if viewer is owner of the product or not*/}
      <Grid.Col span={3}>
        <Text size="sm" c="dimmed">
          {t("common:images")}
        </Text>
      </Grid.Col>
      <Grid.Col span={9}>
        <ImageCarousel images={images || []} isLoading={isLoadingImages} />
      </Grid.Col>
      <Grid.Col span={3}>
        <Text size="sm" c="dimmed">
          {t("common:attachments")}
        </Text>
      </Grid.Col>
      <Grid.Col span={9}>
        <DocumentList files={attachments || []} origin={"api"} isLoading={isLoadingAttachments} />
      </Grid.Col>
    </Grid>
  );
};

const ContractDetails = ({ contract }: { contract: Contract }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  // Both parties open this drawer, so the type is from the viewer's side, not the creator's.
  const isBuy = getContractTradeType(contract, currentCompany) === ContractTradeTypeEnum.BUY;
  const isAutomaticApproval = contract.automaticSalesApproval;
  const { data: contractResponsible, isLoading: isLoadingContractResponsible } = useGetUserById(
    contract.contractManagerId,
    { enabled: !!contract.contractManagerId }
  );

  return (
    <Grid>
      <DetailRow
        label="common:type"
        value={isBuy ? t("contracts:purchaseContract") : t("contracts:salesContract")}
        subValue={isAutomaticApproval ? t("contracts:automaticApproval") : t("contracts:manualApproval")}
        isLoading={false}
      />
      <DetailRow
        label="contracts:startDate"
        value={contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : null}
        isLoading={false}
      />
      {contract.contractEndDate ? (
        <DetailRow
          label="contracts:endDate"
          value={contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : null}
          isLoading={false}
        />
      ) : (
        <DetailRow label="common:amount" value={contract.amount + " " + contract.amountUnit.unit} isLoading={false} />
      )}
      <DetailRow
        label="common:responsible"
        value={contractResponsible ? `${contractResponsible.firstname} ${contractResponsible.lastname}` : null}
        isLoading={isLoadingContractResponsible}
      />
      <DetailRow
        label="common:contact"
        value={contract?.contact.email}
        subValue={contract?.contact.phone ? contract.contact.phone : undefined}
        isLoading={false}
      />
      <DetailRow
        label="contracts:contactCounterparty"
        value={contract?.counterpartyContact.email}
        subValue={contract?.counterpartyContact.phone ? contract.counterpartyContact.phone : undefined}
        isLoading={false}
      />
    </Grid>
  );
};

const PriceAndInvoicingDetails = ({ contract }: { contract: Contract }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  const { currencyType, priceUnit, companySpecificProduct } = contract.contractProduct;
  // The buyer's price includes the platform fees, so each party sees the price they pay or receive.
  const agreedPricePrUnit =
    getContractTradeType(contract, currentCompany) === ContractTradeTypeEnum.BUY
      ? contract.contractProduct.agreedBuyerPricePrUnit
      : contract.contractProduct.agreedSellerPricePrUnit;
  const pricePerUnit = agreedPricePrUnit[currencyType];
  // The amount is in the amount unit (e.g. tonn) but the price is per price unit (e.g. kg).
  const amountInPriceUnit =
    contract.contractType === ContractEndTypeEnum.AMOUNT && contract.amountUnit
      ? convertAmountBetweenUnits(contract.amount, contract.amountUnit.unit, priceUnit.unit, companySpecificProduct)
      : null;
  // Rounded to two decimals, so float noise like 100249.99999999999 isn't shown as 100 250.00.
  const totalPrice = amountInPriceUnit === null ? null : Math.round(pricePerUnit * amountInPriceUnit * 100) / 100;

  const { data: companySpecificServicesData } = useGetCompanySpecificServices(contract.createdByCompany, false, {
    enabled: (contract.companySpecificServices ?? []).length > 0,
    staleTime: 1000 * 60 * 5,
  });
  // The contract only stores the service id and agreed price, so the name and unit come from the creator's services.
  // An agreed price of 0 falls back to the service's own price, like it does when an order is created.
  const companySpecificServices = (contract.companySpecificServices ?? []).flatMap((contractService) => {
    const service = companySpecificServicesData?.find((s) => s.id === contractService.companySpecificServiceId);
    return service ? [{ ...service, price: contractService.price || service.price }] : [];
  });

  return (
    <Grid>
      <DetailRow
        label="common:price"
        value={<NumberFormatter value={pricePerUnit} suffix={` ${currencyType}/${priceUnit.unit}`} />}
        subValue={
          totalPrice === null ? undefined : (
            <>
              {t("contracts:totalPrice")} <NumberFormatter value={totalPrice} suffix={` ${currencyType}`} />
            </>
          )
        }
        isLoading={false}
      />
      {/* <DetailRow label="contracts:includedServices" value={""} isLoading={false} t={t} /> */}
      <DetailRow
        label="contracts:invoicing"
        value={t("enumerables:invoicePaymentIntervals." + contract.invoiceInterval.code)}
        isLoading={false}
      />
      <DetailRow
        label="contracts:due"
        value={contract.invoiceDueDate.days + " " + t("common:days")}
        isLoading={false}
      />
      <SelectedServicesDetails
        services={contract.selectedServices || []}
        companySpecificServices={companySpecificServices}
        t={t}
      />
    </Grid>
  );
};

const SelectedServicesDetails = ({
  services,
  companySpecificServices,
  t,
}: {
  services: PlatformService[];
  companySpecificServices: CompanySpecificService[];
  t: any;
}) => {
  return (
    <>
      <Grid.Col span={3}>
        <Text size="sm" c="dimmed">
          {t("contracts:extraServices")}
        </Text>
      </Grid.Col>
      <Grid.Col span={9}>
        {(services.length > 0 || companySpecificServices.length > 0) && (
          <Stack gap={0}>
            {services.map((service) => (
              <Group justify="space-between" gap={2} key={service.id}>
                <Text size="sm">{t(`enumerables:additionalServices.${service.name}`)}</Text>
                <Text size="xs" c="dimmed">
                  (
                  {service.platformServiceUnit.code === "PROSENT" ? (
                    <>
                      {service.priceOrPercent}% {t("common:ofTotalPrice")}
                    </>
                  ) : (
                    <>
                      {service.priceOrPercent} {service.currencyType}/
                      {service.platformServiceUnit.code.toLocaleLowerCase()}
                    </>
                  )}
                  )
                </Text>
              </Group>
            ))}
            {companySpecificServices.map((service) => (
              <Group justify="space-between" gap={2} key={`company-specific-${service.id}`}>
                <Text size="sm">{service.name}</Text>
                <Text size="xs" c="dimmed">
                  ({service.price} {service.currency}{" "}
                  {t(`enumerables:paymentIntervals.${service.priceUnit.code}`).toLowerCase()})
                </Text>
              </Group>
            ))}
          </Stack>
        )}
      </Grid.Col>
    </>
  );
};

const TransportDetails = ({ contract }: { contract: Contract }) => {
  const getWeightResponsibleName = (weightResponsible: "BUYER" | "SELLER", contract: Contract) => {
    if (weightResponsible === "BUYER") {
      return contract.contractBuyers[0].companyName;
    }
    return contract.contractSellers[0].companyName;
  };

  const getTransportResponsibleName = (transportResponsible: "BUYER" | "SELLER" | "REVIVER", contract: Contract) => {
    if (transportResponsible === "BUYER") {
      return contract.contractBuyers[0].companyName;
    }
    if (transportResponsible === "SELLER") {
      return contract.contractSellers[0].companyName;
    }
    return "Reviver";
  };

  const toAddress = contract.transportToAddress;
  const fromAddress = contract.transportFromAddress;

  return (
    <Grid>
      <DetailRow
        label="contracts:transportResponsible"
        value={getTransportResponsibleName(contract.transportResponsible, contract)}
        isLoading={false}
      />
      <DetailRow
        label="contracts:weightResponsible"
        value={getWeightResponsibleName(contract.weightResponsible, contract)}
        isLoading={false}
      />
      <DetailRow
        label={"contracts:deliverTo"}
        value={toAddress?.address}
        subValue={toAddress?.place + ", " + toAddress?.postCode}
        isLoading={false}
      />
      <DetailRow
        label={"contracts:deliverFrom"}
        value={fromAddress?.address}
        subValue={fromAddress?.place + ", " + fromAddress?.postCode}
        isLoading={false}
      />
    </Grid>
  );
};
